/**
 * MANAGEMENT (15) — analyzer-fleet presentation logic. PURE (no React, no server): the statut/health tone
 * mappers, the relative "last-seen" formatter, TAT/uptime/count formatters, and the health-window option set
 * the fleet table + the drill-down drawer consume. Token colors are returned as `var(--nv-*)` (no raw hex,
 * L-2). Kept in a lib (not a screen) so the table + the charts share them without tripping the react-refresh
 * single-export rule.
 *
 * L-2 — the health-window choices + the error/uptime WARN lines live in ONE frozen {@link FLEET_CONFIG} block
 * (mirrors the sibling metrics.lib ALERT_CONFIG posture: no magic number scattered through the derivation).
 * These client thresholds only TINT a chip; the authoritative freshness/health verdicts are the server's.
 */
import type { BadgeTone } from "@/ui";
import { token } from "@/design/theme";
import type { AnalyzerStatut } from "./analyzers.types";

/** L-2 — the fleet-console tunables. Frozen; no magic number in the derivations below. */
export const FLEET_CONFIG = Object.freeze({
  /** The health drill-down windows the picker offers (trailing days). */
  HEALTH_WINDOWS: [7, 30, 90] as const,
  /** Default window when a drawer first opens. */
  DEFAULT_WINDOW_DAYS: 30,
  /** Server page size for the fleet list — one server page = one rendered table page (no client re-slice). */
  PAGE_SIZE: 25,
  /** Error-rate% at/above this tints the error chip warn. */
  ERROR_WARN_PCT: 2,
  /** Error-rate% at/above this escalates the error chip to danger. */
  ERROR_DANGER_PCT: 5,
  /** Uptime% at/below this tints the uptime chip warn. */
  UPTIME_WARN_PCT: 98,
  /** Uptime% at/below this escalates the uptime chip to danger. */
  UPTIME_DANGER_PCT: 90,
});

export type HealthWindow = (typeof FLEET_CONFIG.HEALTH_WINDOWS)[number];

// ── Number / date formatting (FR locale; figures stay LTR via .nv-ltr-island in the markup) ──

const nfInt = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 });
const nfPct1 = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 });

/** Whole-number count, FR-grouped. */
export function fmtCount(value: number): string {
  return nfInt.format(value);
}

/** Percentage with up to one decimal + the unit; "—" when null. */
export function fmtPct(value: number | null): string {
  return value == null ? "—" : `${nfPct1.format(value)} %`;
}

/**
 * A turnaround duration (ms) → a compact human string (ms / s / min); "—" when null. Keeps the numeric face
 * LTR-friendly (short unit, no locale-specific separator drama).
 */
export function fmtTat(ms: number | null): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${nfInt.format(ms)} ms`;
  const s = ms / 1000;
  if (s < 60) return `${nfPct1.format(s)} s`;
  return `${nfPct1.format(s / 60)} min`;
}

/** Short axis label for a day bucket (FR day/month). */
export function shortDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "2-digit" }).format(d);
}

/**
 * A relative "last-seen" string from an ISO instant (e.g. "il y a 3 min", "il y a 2 h", "il y a 5 j"). Never
 * throws — a null/invalid instant returns a sentinel. `now` is injectable so the unit test is deterministic.
 */
export function relativeSince(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "jamais vu";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "jamais vu";
  const diffMs = now.getTime() - then.getTime();
  if (diffMs < 0) return "à l'instant";
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days} j`;
}

// ── Tone mappers (color + glyph + text — never color alone; daltonisme + AA) ─────────────

/** Badge tone per analyzer lifecycle statut (actif → success · maintenance → warn · hors_service → danger). */
export function analyzerStatutTone(statut: AnalyzerStatut | null | undefined): BadgeTone {
  switch (statut) {
    case "actif":
      return "success";
    case "maintenance":
      return "warn";
    case "hors_service":
      return "danger";
    default:
      return "neutral";
  }
}

/** The heartbeat health chip: online → success · has-agent-but-stale → warn · never-checked-in → neutral. */
export function heartbeatTone(online: boolean, hasAgent: boolean): BadgeTone {
  if (online) return "success";
  return hasAgent ? "warn" : "neutral";
}

/** The heartbeat glyph (carries meaning without color): online ● · stale ◐ · no agent ○. */
export function heartbeatGlyph(online: boolean, hasAgent: boolean): string {
  if (online) return "●";
  return hasAgent ? "◐" : "○";
}

/** Error-rate% → a tone (null/none → neutral). */
export function errorRateTone(pct: number | null): BadgeTone {
  if (pct == null) return "neutral";
  if (pct >= FLEET_CONFIG.ERROR_DANGER_PCT) return "danger";
  if (pct >= FLEET_CONFIG.ERROR_WARN_PCT) return "warn";
  return "success";
}

/** Uptime% → a tone (null → neutral). */
export function uptimeTone(pct: number | null): BadgeTone {
  if (pct == null) return "neutral";
  if (pct <= FLEET_CONFIG.UPTIME_DANGER_PCT) return "danger";
  if (pct <= FLEET_CONFIG.UPTIME_WARN_PCT) return "warn";
  return "success";
}

// ── Chart token colors (token-driven, L-2) ───────────────────────────────────────────────

/** The token color for each health-chart series (result volume vs error volume vs TAT vs uptime). */
export const HEALTH_COLORS = Object.freeze({
  results: token.azure,
  errors: token.danger,
  tat: token.info,
  uptime: token.success,
});
